import { ChevronsRight } from 'lucide-react'
import { getLaneColor } from '../../utils'
import { MULTI_CARD_MIME, parseDragCardIds } from '../../utils/cardSelection'
import { KANBAN_ALLOCATE_EMAIL_MIME } from '../KanbanEmailAllocationRail'
import { KANBAN_CARD_MIME, KANBAN_PLACEMENT_MIME, laneDisplayCount } from './constants'
import type { KanbanLaneProps } from './types'
import type { LaneVM } from './useKanbanLane'

export function LaneCollapsed({ props, vm }: { props: KanbanLaneProps; vm: LaneVM }) {
  const { lane, cards, onCardDrop, onToggleCollapse, onLaneEmailAllocate } = props
  const { dragOver, setDragOver, overWipLimit } = vm
  return (
      <div
        data-lane-id={lane.id}
        data-lane-kind={lane.kind}
        data-collapsed=""
        data-drag-over={dragOver || undefined}
        onDragOver={(e) => {
          // Card-move drags are always honoured. Email-allocate
          // drags only count when the host wired
          // `onLaneEmailAllocate` — without it dropping an email
          // onto an empty lane has nowhere to land.
          const types = e.dataTransfer.types
          const isCard = types.includes(KANBAN_CARD_MIME)
          const isEmail =
            !!onLaneEmailAllocate && types.includes(KANBAN_ALLOCATE_EMAIL_MIME)
          if (!isCard && !isEmail) return
          e.preventDefault()
          e.dataTransfer.dropEffect = isEmail ? 'link' : 'move'
          if (!dragOver) setDragOver(true)
        }}
        onDragLeave={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
          setDragOver(false)
        }}
        onDrop={(e) => {
          const types = e.dataTransfer.types
          if (
            onLaneEmailAllocate &&
            types.includes(KANBAN_ALLOCATE_EMAIL_MIME)
          ) {
            const emailId = e.dataTransfer.getData(KANBAN_ALLOCATE_EMAIL_MIME)
            if (!emailId) return
            e.preventDefault()
            setDragOver(false)
            const suggested = e.dataTransfer.getData('text/plain') || undefined
            void onLaneEmailAllocate(lane, emailId, suggested)
            // Auto-expand so the freshly-created card is visible.
            onToggleCollapse?.(lane)
            return
          }
          const cardId = e.dataTransfer.getData(KANBAN_CARD_MIME)
          if (!cardId) return
          e.preventDefault()
          setDragOver(false)
          const ids = parseDragCardIds(e.dataTransfer.getData(MULTI_CARD_MIME), cardId)
          // ARC-10 — forward the placement id (collapsed lanes have no
          // geometry, so no position; the presence appends at the bottom).
          const placementId =
            e.dataTransfer.getData(KANBAN_PLACEMENT_MIME) || undefined
          if (onCardDrop)
            onCardDrop(
              lane,
              cardId,
              ids.length > 1 ? ids : undefined,
              undefined,
              placementId,
              // ARC-10 MR3 — Alt-drop links (adds a presence) here too.
              e.altKey || undefined,
            )
          // Auto-expand so the operator sees the card land in its new home.
          onToggleCollapse?.(lane)
        }}
        style={{
          background: 'var(--lane-bg, transparent)',
          borderColor: 'var(--board-bg, transparent)',
        }}
        className={[
          // FIX-2026-07-27-KANBAN-CARD-PRESS-SNAPPY (sibling of KanbanLane):
          // drag-over only changes border, background and shadow, so `all`
          // bought nothing and put every layout utility on this element into
          // the animated set.
          'group/lane relative flex h-full w-10 shrink-0 flex-col items-center rounded-lg border transition-[box-shadow,border-color,background-color] duration-150',
          'bg-neutral-100 dark:bg-zinc-900/80',
          dragOver
            ? 'border-blue-500 dark:border-blue-400 bg-blue-50/60 dark:bg-blue-950/40 shadow-md'
            : 'border-neutral-200/80 dark:border-zinc-800',
        ].join(' ')}
        title={`${lane.name} (${laneDisplayCount(lane.card_count, cards.length).toLocaleString()}) · click to expand`}
      >
        <div
          className="absolute top-0 left-0 right-0 h-0.5 rounded-t-lg"
          style={{ background: getLaneColor(lane) }}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => onToggleCollapse?.(lane)}
          aria-label={`Expand ${lane.name}`}
          className="mt-1 rounded p-0.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200 dark:hover:bg-zinc-800"
        >
          <ChevronsRight className="w-3.5 h-3.5" />
        </button>
        {/* Rotated title — uses writing-mode for proper baseline,
            falls back to a transform on browsers without it. The
            count stays horizontal at the bottom for legibility. */}
        <div
          className="flex-1 mt-2 flex items-center justify-center min-h-0"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          <span
            // BUG-2026-05-17-KANBAN-THEMED-LANE-TITLE-INVISIBLE —
            // same `--lane-fg` consumption as the expanded title
            // above; collapsed lanes render against the same themed
            // background and were equally invisible on JOB / TRELLO
            // boards.
            style={{ color: 'var(--lane-fg, currentColor)' }}
            className="truncate text-[12px] font-semibold text-neutral-700 dark:text-neutral-200 max-h-[260px]"
            title={lane.name}
          >
            {lane.name}
          </span>
        </div>
        <span
          className={[
            'mb-1.5 mt-1 inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
            overWipLimit
              ? 'bg-red-500/20 text-red-700 dark:text-red-300'
              : 'bg-neutral-200 dark:bg-zinc-800 text-neutral-700 dark:text-zinc-200',
          ].join(' ')}
          title={lane.wip_limit > 0 ? `WIP limit: ${lane.wip_limit}` : undefined}
        >
          {/* BUG-2026-07-06-KANBAN-LANE-COUNT-CAPPED: same server-truth
              rule as the expanded header's count chip. */}
          {laneDisplayCount(lane.card_count, cards.length).toLocaleString()}{lane.wip_limit > 0 ? `/${lane.wip_limit}` : ''}
        </span>
      </div>
  )
}
